/**
 * GoogleImportDialog — dedupe-aware Google Contacts import (Feb 2026, iter 286).
 *
 * Uses the EXISTING Phase-4 dedupe-aware sync endpoints:
 *   • GET  /api/auth/google/status   — Google-connected?
 *   • GET  /api/auth/google/login    — start OAuth (redirect)
 *   • POST /api/google-contacts/sync/preview   — dry-run breakdown, no writes
 *   • POST /api/google-contacts/sync/apply     — {confirm:true} → writes to
 *     personal tier, snapshots to db.contact_import_backups, returns batch_id
 *
 * Guarantees (all in the backend already — surfaced here, never weakened):
 *   • One-way (no writes back to Google)
 *   • Personal-tier default (tenant directory NEVER auto-grows)
 *   • Every contact runs through services.contact_dedupe.classify_pair
 *   • Cross personal↔tenant boundary ALWAYS flags — never auto-merges
 *   • Idempotent on Google resourceName (stored as external_ref)
 *   • Reversible: every apply snapshots under one batch_id
 *
 * v1 scope: connect → preview → apply → success + flag count.
 * v2 seams (called out in UI copy, not built here):
 *   • Rollback UI (batch_id is returned; endpoint TBD)
 *   • Scheduled sync
 *   • Full review-queue table for cross-boundary flags
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { startGoogleOAuth } from '../utils/googleOAuth';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Globe, ShieldCheck, AlertTriangle, CheckCircle2, GitMerge, RefreshCw, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GoogleImportDialog({ open, onOpenChange, onImported }) {
  // Flow phase: 'checking' | 'not_connected' | 'ready' | 'previewing' | 'preview_ready' | 'applying' | 'done'
  const [phase, setPhase] = useState('checking');
  const [status, setStatus] = useState({});
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  // Phase 2 create-sync (Feb 2026, iter 287): opt-in toggle for pushing
  // newly-created PERSONAL-tier contacts back to Google Contacts.
  const [prefs, setPrefs] = useState({ push_new_contacts_to_google: false });
  const [prefsSaving, setPrefsSaving] = useState(false);
  // Iter 290 — retroactive push + sync-log viewer.
  const [pushingExisting, setPushingExisting] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [syncLogLoading, setSyncLogLoading] = useState(false);

  // On open, check /auth/google/status + load sync prefs.
  useEffect(() => {
    if (!open) return;
    setPhase('checking');
    setPreview(null);
    setResult(null);
    setConfirm(false);
    axios.get(`${API}/auth/google/status`)
      .then(r => {
        setStatus(r.data || {});
        setPhase(r.data?.connected ? 'ready' : 'not_connected');
        if (r.data?.connected) {
          axios.get(`${API}/auth/google/sync-prefs`)
            .then(pr => setPrefs(pr.data || { push_new_contacts_to_google: false }))
            .catch(() => { /* non-fatal */ });
        }
      })
      .catch(err => {
        console.error('[GoogleImport] status check failed:', err);
        setPhase('not_connected');
      });
  }, [open]);

  const savePushPref = async (next) => {
    setPrefsSaving(true);
    try {
      const r = await axios.put(`${API}/auth/google/sync-prefs`, {
        push_new_contacts_to_google: next,
      });
      setPrefs(p => ({ ...p, push_new_contacts_to_google: !!(r.data?.push_new_contacts_to_google ?? next) }));
      toast.success(next
        ? 'Push new personal contacts to Google — ON'
        : 'Push new personal contacts to Google — OFF'
      );
    } catch (err) {
      console.error('[GoogleImport] prefs save failed:', err);
      toast.error(`Could not save preference: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPrefsSaving(false);
    }
  };

  // Iter 290 — retroactively push every personal-tier contact the caller
  // owns that isn't already linked to a Google resourceName. Idempotent.
  const pushExistingNow = async () => {
    setPushingExisting(true);
    setPushResult(null);
    try {
      const r = await axios.post(`${API}/google-contacts/push-existing`, {
        all_owned_personal: true,
      });
      setPushResult(r.data);
      if (r.data?.pushed > 0) {
        toast.success(`Pushed ${r.data.pushed} contact${r.data.pushed === 1 ? '' : 's'} to Google.`);
      } else if (r.data?.considered === 0) {
        toast.info('No personal-tier contacts to push. Tick "Personal contact" on the ones you want to sync.');
      } else {
        toast.warning(`0 pushed of ${r.data?.considered} considered. Open Recent sync activity below for details.`);
      }
      // Refresh sync log so the user immediately sees what happened.
      loadSyncLog();
    } catch (err) {
      console.error('[GoogleImport] push-existing failed:', err);
      toast.error(`Push failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPushingExisting(false);
    }
  };

  // Iter 290 — Fetch last 20 sync-log entries. Lazy — only loads when
  // the <details> block is opened.
  const loadSyncLog = async () => {
    setSyncLogLoading(true);
    try {
      const r = await axios.get(`${API}/google-contacts/sync-log`, { params: { limit: 20 } });
      setSyncLog(r.data?.entries || []);
    } catch (err) {
      console.error('[GoogleImport] sync-log fetch failed:', err);
    } finally {
      setSyncLogLoading(false);
    }
  };

  const connectGoogle = () => {
    // Full redirect — Google OAuth needs the top-level location. Return path
    // is captured server-side via state so we land back on /contacts after
    // the callback completes.
    //
    // IMPORTANT: window.location.href navigations cannot carry the
    // Authorization: Bearer header the API normally uses. We pass the JWT
    // as a `token` query param so /api/auth/google/login can identify the
    // caller. The endpoint accepts JWT via either header (XHR path) or
    // ?token= (top-level nav path). Token is short-lived (~15 min) and
    // never leaves KEY-MADE — Google never sees it.
    startGoogleOAuth({ redirectPath: window.location.pathname });
  };

  const runPreview = async () => {
    setPhase('previewing');
    try {
      const r = await axios.post(`${API}/google-contacts/sync/preview`);
      setPreview(r.data);
      setPhase('preview_ready');
    } catch (err) {
      console.error('[GoogleImport] preview failed:', err);
      const detail = err.response?.data?.detail || err.message || 'Preview failed';
      if (err.response?.status === 403) {
        toast.error('Contacts permission not granted. Reconnect Google to add contacts access.');
        setPhase('not_connected');
      } else {
        toast.error(`Preview failed: ${detail}`);
        setPhase('ready');
      }
    }
  };

  const applyImport = async () => {
    if (!confirm) return;
    setPhase('applying');
    try {
      const r = await axios.post(`${API}/google-contacts/sync/apply`, { confirm: true });
      setResult(r.data);
      setPhase('done');
      const counts = r.data?.counts || {};
      const flagCount = (counts.cross_boundary_flag || 0) + (counts.flag_other || 0);
      toast.success(
        `Imported ${counts.new_personal_imports || 0} contact(s)`
        + (counts.auto_merge_personal ? `, merged ${counts.auto_merge_personal}` : '')
        + (flagCount ? `, ${flagCount} flagged for review` : '')
      );
      onImported?.(r.data);
    } catch (err) {
      console.error('[GoogleImport] apply failed:', err);
      toast.error(`Apply failed: ${err.response?.data?.detail || err.message}`);
      setPhase('preview_ready');
    }
  };

  const counts = preview?.counts || {};
  const flagCount = (counts.cross_boundary_flag || 0) + (counts.flag_other || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && phase !== 'applying' && phase !== 'previewing') onOpenChange(false); }}>
      <DialogContent
        className="max-w-2xl bg-[#0F0F0F] border-[#2A2A2A] text-white"
        data-testid="google-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            Import Google Contacts
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            One-way import from your Google account. All new contacts land at
            <strong className="text-white"> personal tier</strong> — the tenant directory never auto-grows.
            Cross-boundary matches (personal ↔ tenant) are always flagged, never auto-merged.
          </DialogDescription>
        </DialogHeader>

        {/* Guarantees strip — always visible so users know the rules. */}
        <Alert className="bg-[#1A1A1A] border-[#2A2A2A]">
          <ShieldCheck className="h-4 w-4 text-green-400" />
          <AlertDescription className="text-xs text-gray-400 space-y-1">
            <div>• Import is one-way (Google → KEY-MADE personal tier).</div>
            <div>• Outbound push is <strong>opt-in</strong> and <strong>personal-tier only</strong> — tenant contacts never leave KEY-MADE.</div>
            <div>• Every contact runs through the KEY-MADE dedupe classifier.</div>
            <div>• Cross personal↔tenant boundary <strong>always</strong> flags.</div>
            <div>• Re-runnable safely (idempotent by Google resourceName + push-echo skip).</div>
            <div>• Reversible — every apply snapshots under a batch_id.</div>
          </AlertDescription>
        </Alert>

        {phase === 'checking' && (
          <div className="text-center py-6 text-sm text-gray-400" data-testid="google-import-checking">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Checking Google connection…
          </div>
        )}

        {phase === 'not_connected' && (
          <div className="text-center py-4 space-y-3" data-testid="google-import-not-connected">
            <p className="text-sm text-gray-300">Google account not connected yet.</p>
            <Button
              onClick={connectGoogle}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="google-import-connect-btn"
            >
              <Globe className="w-4 h-4 mr-2" />
              Connect Google
            </Button>
            <p className="text-xs text-gray-500">
              You&apos;ll be redirected to Google to grant the <code>contacts</code> (read + write)
              scope. Read powers this import; write powers the optional
              &ldquo;push new contacts to Google&rdquo; toggle below. After consent, come back
              here to run the dry-run preview.
            </p>
          </div>
        )}

        {/* Phase 1 reconnect banner — token was granted under the OLD
            contacts.readonly scope; user must re-consent to enable the
            two-way sync's outbound writes. */}
        {status.connected && status.needs_scope_upgrade && (phase === 'ready' || phase === 'preview_ready') && (
          <Alert
            className="bg-amber-950/40 border-amber-700"
            data-testid="google-import-scope-upgrade-banner"
          >
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <AlertDescription className="text-xs text-amber-200 space-y-2">
              <div>
                <strong>Your Google connection needs a scope upgrade</strong> — reconnect to
                enable two-way sync. Import will still work on the old read-only scope, but
                &ldquo;push new contacts to Google&rdquo; is disabled until you re-consent.
              </div>
              <Button
                size="sm"
                onClick={connectGoogle}
                className="bg-amber-700 hover:bg-amber-600 text-white h-7 text-xs"
                data-testid="google-import-reconnect-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Reconnect Google
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {phase === 'ready' && (
          <div className="space-y-3" data-testid="google-import-ready">
            <div className="text-sm text-gray-300">
              Connected as <strong className="text-white">{status.email || 'your Google account'}</strong>
              {status.connected_at && (
                <span className="text-xs text-gray-500 ml-2">since {new Date(status.connected_at).toLocaleDateString()}</span>
              )}
            </div>
            <Button
              onClick={runPreview}
              className="bg-[#70170F] hover:bg-[#70170F]/90 w-full"
              data-testid="google-import-preview-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Preview import (dry-run)
            </Button>

            {/* Phase 2 create-sync toggle — outbound writes are opt-in and
                only fire for PERSONAL-tier contacts. Tenant-tier contacts
                NEVER push to Google (hard boundary). */}
            <div
              className="flex items-start justify-between gap-3 p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]"
              data-testid="google-push-toggle-row"
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-white">Push new contacts to Google</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  When ON, every new <strong className="text-gray-300">personal-tier</strong> contact you create in
                  KEY-MADE is also created in your Google Contacts. Tenant (firm) contacts are
                  never synced to Google. Requires the reconnect above if your token is on the
                  old read-only scope.
                </div>
              </div>
              <label className="flex items-center gap-2 shrink-0">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#70170F]"
                  checked={!!prefs.push_new_contacts_to_google}
                  disabled={prefsSaving || !!status.needs_scope_upgrade}
                  onChange={(e) => savePushPref(e.target.checked)}
                  data-testid="google-push-toggle-checkbox"
                />
                <span className="text-xs text-gray-300">
                  {prefs.push_new_contacts_to_google ? 'ON' : 'OFF'}
                </span>
              </label>
            </div>

            {/* Iter 290 — retroactive push. Pushes ALL personal-tier
                contacts owned by the user that don't yet have an
                external_ref. Idempotent — safe to click repeatedly.
                Uses the same writer + guards as the create hook. */}
            <div className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A] space-y-2">
              <div className="text-xs font-medium text-white">
                Retroactively push existing personal contacts
              </div>
              <div className="text-[11px] text-gray-500">
                Sends every personal-tier contact you own that isn&apos;t already linked to
                a Google entry. Use this to backfill contacts you created before
                enabling the toggle. Idempotent.
              </div>
              <Button
                size="sm"
                onClick={pushExistingNow}
                disabled={pushingExisting || !!status.needs_scope_upgrade}
                className="bg-[#70170F] hover:bg-[#70170F]/90 h-8 text-xs w-full"
                data-testid="google-push-existing-btn"
              >
                {pushingExisting ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Pushing…</>
                ) : (
                  <><Users className="w-3 h-3 mr-1" />Push all my personal contacts to Google now</>
                )}
              </Button>
              {pushResult && (
                <div className="text-[11px] mt-2 space-y-1" data-testid="google-push-existing-result">
                  <div className="text-gray-300">
                    Considered: <strong>{pushResult.considered}</strong> ·
                    Pushed: <strong className="text-emerald-400">{pushResult.pushed}</strong> ·
                    Skipped: <strong className="text-amber-400">{pushResult.skipped}</strong>
                  </div>
                  {pushResult.results?.filter(r => r.status !== 'ok').slice(0, 5).map((r, i) => (
                    <div key={i} className="text-amber-300 truncate">
                      • {r.contact_id?.slice(0, 8)}… — {r.status}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Iter 290 — sync-log viewer. One click surfaces the last 20
                outbound entries with api_status + error_detail so users
                can diagnose without shell access. */}
            <details className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
              <summary
                className="text-xs font-medium text-white cursor-pointer"
                onClick={loadSyncLog}
                data-testid="google-sync-log-toggle"
              >
                Recent sync activity {syncLog.length ? `(${syncLog.length})` : ''}
              </summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="google-sync-log-list">
                {syncLogLoading && (
                  <div className="text-[11px] text-gray-500">Loading…</div>
                )}
                {!syncLogLoading && syncLog.length === 0 && (
                  <div className="text-[11px] text-gray-500">
                    No entries yet. Create a personal contact to see one appear here.
                  </div>
                )}
                {syncLog.map((row, i) => (
                  <div key={i} className="text-[11px] font-mono flex items-center gap-2">
                    <span className={row.api_status === 'ok' ? 'text-emerald-400' : 'text-amber-300'}>
                      {row.api_status}
                    </span>
                    <span className="text-gray-500 truncate flex-1">
                      {row.direction}/{row.action} · {row.resource_name || row.error_detail?.slice(0, 40) || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {phase === 'previewing' && (
          <div className="text-center py-6 text-sm text-gray-400" data-testid="google-import-previewing">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Fetching your Google contacts and running dedupe classification…
          </div>
        )}

        {(phase === 'preview_ready' || phase === 'applying') && preview && (
          <div className="space-y-3" data-testid="google-import-preview-ready">
            <div className="text-sm font-medium text-white">Dry-run breakdown</div>
            <div className="grid grid-cols-2 gap-2">
              <PreviewCard
                icon={<Users className="w-4 h-4 text-green-400" />}
                label="Net-new (→ personal)"
                value={counts.new_personal_imports || 0}
                testid="google-import-count-new"
              />
              <PreviewCard
                icon={<GitMerge className="w-4 h-4 text-blue-400" />}
                label="Auto-merge into personal"
                value={counts.auto_merge_personal || 0}
                testid="google-import-count-merge"
              />
              <PreviewCard
                icon={<AlertTriangle className="w-4 h-4 text-amber-400" />}
                label="Cross-boundary flags"
                value={counts.cross_boundary_flag || 0}
                testid="google-import-count-cross-flag"
                accent="amber"
              />
              <PreviewCard
                icon={<AlertTriangle className="w-4 h-4 text-yellow-400" />}
                label="Personal-tier flags"
                value={counts.flag_other || 0}
                testid="google-import-count-flag"
              />
              <PreviewCard
                icon={<RefreshCw className="w-4 h-4 text-gray-400" />}
                label="Existing (idempotent update)"
                value={counts.existing_imports || 0}
                testid="google-import-count-idempotent"
              />
              <PreviewCard
                icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}
                label="Skipped (our own push echo)"
                value={counts.skipped_echo || 0}
                testid="google-import-count-skipped-echo"
                accent="green"
              />
              <PreviewCard
                icon={<ShieldCheck className="w-4 h-4 text-purple-400" />}
                label="Tenant directory growth"
                value={preview?.tenant_directory_will_grow_by || 0}
                testid="google-import-count-tenant-growth"
                accent="green"
              />
            </div>

            {counts.cross_boundary_flag > 0 && (
              <Alert className="bg-amber-950/40 border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-xs text-amber-200">
                  <strong>{counts.cross_boundary_flag}</strong> contact(s) match an existing tenant record.
                  They will still be imported to your personal tier so you don&apos;t lose them, but
                  each is flagged for you to reconcile from the review queue (v2).
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-start gap-2 p-3 rounded bg-[#1A1A1A]">
              <Checkbox
                id="google-import-confirm"
                checked={confirm}
                onCheckedChange={(v) => setConfirm(!!v)}
                data-testid="google-import-confirm-checkbox"
              />
              <label htmlFor="google-import-confirm" className="text-xs text-gray-300 cursor-pointer">
                I&apos;ve reviewed the breakdown above. Apply the import — writes to my personal contacts only.
              </label>
            </div>
          </div>
        )}

        {phase === 'applying' && (
          <div className="text-center py-2 text-sm text-gray-400" data-testid="google-import-applying">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Applying import + snapshotting for rollback…
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-3" data-testid="google-import-done">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Import complete</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded bg-[#1A1A1A]"><strong className="text-white">{result.counts?.new_personal_imports || 0}</strong> new</div>
              <div className="p-2 rounded bg-[#1A1A1A]"><strong className="text-white">{result.counts?.auto_merge_personal || 0}</strong> merged</div>
              <div className="p-2 rounded bg-[#1A1A1A]"><strong className="text-white">{result.counts?.existing_imports || 0}</strong> updated</div>
              <div className="p-2 rounded bg-[#1A1A1A]"><strong className="text-white">{flagCount}</strong> flagged</div>
            </div>
            <div className="text-[10px] text-gray-500 break-all">
              batch_id: <code className="text-gray-400">{result.batch_id}</code>
              <br />
              Rollback UI ships in v2 — the backup snapshot is safe in <code>db.contact_import_backups</code>.
            </div>
            {flagCount > 0 && (
              <Button variant="outline" size="sm" className="border-amber-600 text-amber-400 hover:bg-amber-900/20" disabled data-testid="google-import-review-flags-btn">
                Review {flagCount} flagged (v2)
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'preview_ready' && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-[#2A2A2A]"
                data-testid="google-import-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={applyImport}
                disabled={!confirm}
                className="bg-[#70170F] hover:bg-[#70170F]/90 disabled:opacity-40"
                data-testid="google-import-apply-btn"
              >
                Apply import
              </Button>
            </>
          )}
          {phase === 'done' && (
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-[#70170F] hover:bg-[#70170F]/90"
              data-testid="google-import-close-btn"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewCard({ icon, label, value, testid, accent }) {
  const bg = accent === 'amber' ? 'bg-amber-950/30' : accent === 'green' ? 'bg-green-950/30' : 'bg-[#1A1A1A]';
  return (
    <div className={`p-3 rounded ${bg} border border-[#2A2A2A]`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-semibold text-white">{value}</div>
    </div>
  );
}
